<!--
 * @Description: 新增开票类目
 * @Date: 2025-09-12 17:56:29
-->
<template>
  <BaseDialog
    width="650px"
    title="新增类目"
    :model-value="true"
    :show-close="true"
    :close-on-press-escape="true"
    :close-on-click-modal="false"
    :show-footer="false"
  >
    <BaseTextContent>
      <BaseForm ref="formRef" label-position="right" :model="formData">
        <BaseFormItem
          label="一级类目"
          prop="id"
          :rules="[{ required: true, trigger: 'change', message: '请选择' }]"
        >
          <BaseSelect
            placeholder="请选择"
            v-model="formData.id"
            :filterUseRemote="true"
            :init="true"
            :remoteMethod="val => getInvoiceItemRemoteMethod(val, 1)"
            @change="onInvoiceItemChange"
          />
        </BaseFormItem>
        <BaseFormItem
          v-for="(subItem, index) in formData.subItem"
          :key="index"
          :label="
            formData.subItem.length > 1 ? '二级类目' + (index + 1) : '二级类目'
          "
          :prop="'subItem.' + index + '.id'"
          :rules="[{ required: true, trigger: 'change', message: '请选择' }]"
        >
          <div
            style="display: flex; align-items: center; gap: 10px; width: 100%"
          >
            <BaseSelect
              style="flex: 1"
              placeholder="请选择"
              labelKey="name"
              valueKey="id"
              :disabled="!formData.id"
              :filterUseRemote="true"
              :init="false"
              :remoteMethod="val => getInvoiceItemRemoteMethod(val, 2, index)"
              v-model="subItem.id"
              @change="(val, [option]) => onSubItemChange(index, option)"
            />
            <BaseButton
              v-if="formData.subItem.length > 1"
              type="text"
              @click="removeSubItem(index)"
            >
              删除
            </BaseButton>
          </div>
        </BaseFormItem>
        <BaseButton
          type="text"
          @click="addSubItem"
          :disabled="!formData.id || !canAddSubItem"
        >
          添加二级类目
        </BaseButton>
      </BaseForm>
    </BaseTextContent>
    <template #footer>
      <BaseButton type="default" @click="onCancel">取消</BaseButton>
      <BaseButton type="primary" :disabled="!isValid" :click="onConfirm">
        保存
      </BaseButton>
    </template>
  </BaseDialog>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import {
  BaseForm,
  BaseButton,
  BaseTextContent,
  XrkMessage,
  BaseDialog,
  BaseFormItem,
  BaseSelect
} from 'xrk-components';
import { getInvoiceItem, getInvoiceSubItem } from '../../data-source/apis';
import { GetInvoiceItemDataItem } from '../../data-source/types';

const emits = defineEmits(['success']);
const props = withDefaults(
  defineProps<{
    remove?: () => {};
    invoiceItemIdList: GetInvoiceItemDataItem[];
  }>(),
  {}
);

const invoiceAjax: Record<number, typeof getInvoiceItem> = {
  1: getInvoiceItem,
  2: getInvoiceSubItem
};

const formRef = ref();
const formData = ref<GetInvoiceItemDataItem>({
  id: null,
  code: '',
  topLevelCategory: '',
  subcategory: '',
  label: '',
  value: null,
  disabled: false,
  subItem: [{ id: null, name: '' }],
  name: null
});

const isValid = computed(() => {
  return (
    formData.value.id != null &&
    formData.value.subItem.length > 0 &&
    formData.value.subItem.every(s => s.id != null)
  );
});

// 所有二级类目都已填写才能新增下一行
const canAddSubItem = computed(() =>
  formData.value.subItem.every(s => s.id != null)
);

// 一级类目已选 ID 集合
const selectedIds1 = computed(() =>
  props.invoiceItemIdList.map(item => item.id)
);

const getInvoiceItemRemoteMethod = async (
  val: string,
  type: 1 | 2,
  subIndex?: number
) => {
  const resp = await invoiceAjax[type]({
    keyword: val,
    ...(formData.value.id && { itemId: formData.value.id })
  });

  if (type === 1) {
    const taken = new Set(selectedIds1.value);
    resp.data.forEach(item => {
      if (taken.has(item.id)) item.disabled = true;
    });
  } else if (type === 2 && subIndex !== undefined) {
    const taken = new Set<number>();
    // 已有 invoiceItemIdList 中的二级类目
    props.invoiceItemIdList.forEach(item => {
      if (item.subItem) {
        item.subItem.forEach(s => taken.add(s.id));
      }
    });
    // 当前对话框中其他行的已选值
    formData.value.subItem.forEach((s, i) => {
      if (i !== subIndex && s.id != null) taken.add(s.id);
    });
    resp.data.forEach(item => {
      if (taken.has(item.id)) item.disabled = true;
    });
  }

  return resp;
};

const onInvoiceItemChange = (val, [option]) => {
  formData.value = {
    ...option,
    subItem: [{ id: null, name: '' }]
  };
};

const onSubItemChange = (index: number, option) => {
  formData.value.subItem[index] = { id: option.id, name: option.name };
};

const addSubItem = () => {
  formData.value.subItem.push({ id: null, name: '' });
};

const removeSubItem = (index: number) => {
  formData.value.subItem.splice(index, 1);
};

const onCancel = () => {
  props.remove();
};

const onConfirm = async () => {
  XrkMessage.success('操作成功');
  emits('success', formData.value);
  onCancel();
};
</script>
